import { Component, Input, OnChanges } from '@angular/core';
import { VueServerMessage, VueServerMessageType, VueTranslationService } from '@vue/vueframework';
import { AppDefaultsService } from 'src/app/app-defaults.service';
import { PageMessage } from './page-message.model';

/**
 * This is a boiler plate for VueMessages. Just pass
 * it a single key or keys and it will translate and display
 * the message. Very simple to implement
 */
@Component({
  selector: 'app-page-messages',
  templateUrl: './page-messages.component.html',
  standalone: false
})
export class PageMessagesComponent implements OnChanges {
  /**
   * If there is only one key to translates. Should not be used with messages.
   * This will override messages.
   */
  @Input() public message: string | PageMessage;

  @Input() public messages: (string | PageMessage)[];

  /**
   * App message bundle, defaults to app defaults
   */
  @Input() public bundle: string;

  public renderedMessages: VueServerMessage[] = [];

  public constructor(
    private vueTranslateService: VueTranslationService,
    private defaults: AppDefaultsService) { }

  public ngOnChanges(): void {
    this.renderedMessages = [];
    if (this.message) {
      this.renderedMessages = [ this.convertToMessages(this.message) ];
    }

    if (this.messages) {
      this.renderedMessages = this.messages.map(
        (m) => this.convertToMessages(m)
      );
    }
  }

  public convertToMessages(k: string | PageMessage): VueServerMessage {
    const defaultBundle = this.bundle || this.defaults.getDefaultAppBundle();
    if (typeof k === 'string') {
      return new VueServerMessage({
        type: VueServerMessageType.Error,
        messageText: this.vueTranslateService.getTranslation(defaultBundle, k),
        scrollIntoView: true,
        renderInHtml: true
      });
    }
    return new VueServerMessage({
      type: k.type || VueServerMessageType.Error,
      messageText: this.getTranslationFromObject(k, defaultBundle) || this.getUnknownError(),
      scrollIntoView: true,
      renderInHtml: true,
      ...(k.options || {})
    });
  }

  public getTranslationFromObject(obj: PageMessage, defaultBundle?: string): string {
    if (obj.code) {
      const message = this.vueTranslateService.getTranslationFromServerCode({
        code: obj.code,
        parameters: obj.parameters
      });
      if (!message || message.startsWith('[@@')) {
        return null;
      }
      return message;
    }
    return this.vueTranslateService.getTranslation(
      obj.bundle || defaultBundle, obj.key, obj.parameters
    );
  }

  public getUnknownError(): string {
    return this.vueTranslateService.getTranslation(this.defaults.getDefaultAppBundle(), 'error.UnknownError');
  }
}
